// src/components/profile/ChipEditor.tsx
// A compact multi-value "chip" editor: type a value and press Enter (or comma) to add,
// click × to remove, Backspace on an empty input removes the last chip. Replaces the
// clunky one-text-input-per-row pattern used across the Profile section.

import React, { useState } from 'react';
import { X, Plus } from 'lucide-react';

interface ChipEditorProps {
  label?: string;
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  helpText?: string;
  /** Optional one-click suggestions shown beneath the input. */
  suggestions?: string[];
  recommend?: number; // show a "n/recommend" hint
}

export function ChipEditor({ label, value, onChange, placeholder, helpText, suggestions, recommend }: ChipEditorProps) {
  const [draft, setDraft] = useState('');
  const items = (value || []).filter(Boolean);

  const add = (raw: string) => {
    const v = raw.trim();
    if (!v) return;
    // Allow comma/newline separated paste.
    const parts = v.split(/[,\n]/).map((p) => p.trim()).filter(Boolean);
    const next = [...items];
    for (const p of parts) if (!next.some((x) => x.toLowerCase() === p.toLowerCase())) next.push(p);
    onChange(next);
    setDraft('');
  };

  const remove = (i: number) => onChange(items.filter((_, idx) => idx !== i));

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      add(draft);
    } else if (e.key === 'Backspace' && !draft && items.length) {
      remove(items.length - 1);
    }
  };

  const remaining = suggestions?.filter((s) => !items.some((x) => x.toLowerCase() === s.toLowerCase())) || [];

  return (
    <div>
      {label && (
        <div className="flex items-center justify-between mb-1.5">
          <label className="block text-sm font-medium text-bw-ink">{label}</label>
          {typeof recommend === 'number' && (
            <span className={`text-xs ${items.length >= recommend ? 'text-success' : 'text-bw-muted'}`}>
              {items.length}/{recommend}+ recommended
            </span>
          )}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-1.5 rounded-lg border border-bw-border bg-bw-surface p-2 focus-within:ring-2 focus-within:ring-bw-primary/30">
        {items.map((item, i) => (
          <span key={`${item}-${i}`} className="inline-flex items-center gap-1 rounded-full bg-bw-primary-tint text-bw-primary px-2.5 py-1 text-xs font-medium">
            {item}
            <button type="button" onClick={() => remove(i)} className="hover:text-bw-ink" aria-label={`Remove ${item}`}>
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={() => draft.trim() && add(draft)}
          placeholder={items.length === 0 ? placeholder : 'Add more…'}
          className="flex-1 min-w-[140px] bg-transparent text-sm text-bw-ink focus:outline-none px-1 py-0.5"
        />
      </div>
      {helpText && <p className="text-xs text-bw-muted mt-1">{helpText}</p>}
      {remaining.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-1.5">
          {remaining.slice(0, 8).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => add(s)}
              className="inline-flex items-center gap-1 rounded-full border border-dashed border-bw-border text-bw-muted hover:text-bw-primary hover:border-servet-blue px-2 py-0.5 text-xs"
            >
              <Plus className="h-3 w-3" /> {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default ChipEditor;
